import { useMemo } from "react";
import { DxCodeEntryInput } from "../../../../../components/modals/editDxCodeSidePanel/editDxCodeSidePanel";
import {
  NccnIcdCodeEntry,
  NccnIcdConfig,
} from "../../../../../data-model/nccnIcdConfig";
import { NccnRegimenConfig } from "../../../../../data-model/nccnRegimenConfig";
import { DxCodeConfigTab } from "../../../../../enums/dxCodeConfigTab";

interface EditingConfigData {
  mode: "drug" | "regimen";
  drugName?: string;
  jCode?: string;
  drugType?: string;
  regimenName?: string;
  regimenCode?: string;
  dxCodes: DxCodeEntryInput[];
  hasPriorityConfig: boolean;
}

/**
 * Hook to get editing config data for the currently selected row
 */
/**
 * Creates a priority map from organization-specific priority configuration
 */
const buildPriorityMap = (
  orgPriorityConfig: Record<string, number> | undefined,
): Map<string, number> => {
  const priorityMap = new Map<string, number>();
  if (orgPriorityConfig) {
    Object.entries(orgPriorityConfig).forEach(([dxCode, priority]) => {
      priorityMap.set(dxCode, priority as number);
    });
  }
  return priorityMap;
};

/**
 * Builds and sorts DX codes array from entries.
 *
 * Per-org drug entries carry a per-code `rank` (0 = pinned/direct-override, >0 =
 * priority, absent = unranked) and are mapped straight to the Priority field so
 * they round-trip on save. Regimen entries carry no rank and fall back to the
 * legacy per-org priority map. Rows sort by priority ascending, unranked last.
 */
const buildAndSortDxCodes = (
  entries: NccnIcdCodeEntry[],
  priorityMap: Map<string, number>,
): DxCodeEntryInput[] => {
  const dxCodes: DxCodeEntryInput[] = entries.map((e) => ({
    dxCode: e.code,
    priority: typeof e.rank === "number" ? e.rank : priorityMap.get(e.code),
    source: e.source ?? [],
  }));

  // Sort: rows with a priority first (ascending), then unranked rows
  dxCodes.sort((a, b) => {
    if (a.priority === undefined && b.priority === undefined) return 0;
    if (a.priority === undefined) return 1;
    if (b.priority === undefined) return -1;
    return a.priority - b.priority;
  });

  return dxCodes;
};

/**
 * Whether a per-org drug config carries any rank (drives the "No priority
 * defined" empty state for the drug tab).
 */
const hasDrugRanking = (entries: NccnIcdCodeEntry[]): boolean =>
  entries.some((e) => typeof e.rank === "number");

/**
 * Checks if priority configuration exists for an organization
 */
const hasPriorityConfig = (
  orgPriorityConfig: Record<string, number> | undefined,
): boolean => {
  return !!orgPriorityConfig && Object.keys(orgPriorityConfig).length > 0;
};

/**
 * Gets editing config data for drug mode
 */
const getDrugConfigData = (
  editingRowData: any,
  selectedOrgId: string,
  nccnIcdConfigData: NccnIcdConfig[],
): EditingConfigData | null => {
  const config = nccnIcdConfigData.find(
    (c) => c.id === editingRowData.id || c.code === editingRowData.jCode,
  );
  if (!config) return null;

  const orgPriorityConfig =
    config.priority_config_org_specific?.[selectedOrgId];
  const allEntries = (config.codes || []).filter((e) => e.code?.length > 0);
  const priorityMap = buildPriorityMap(orgPriorityConfig);
  const dxCodes = buildAndSortDxCodes(allEntries, priorityMap);

  return {
    mode: "drug" as const,
    drugName: config.category || "",
    jCode: config.code || config.id || "",
    drugType: config.category || "",
    dxCodes,
    // Per-org drug ranking/pins live in `codes[].rank`, not the legacy overlay.
    hasPriorityConfig: hasDrugRanking(allEntries),
  };
};

/**
 * Gets editing config data for regimen mode
 */
const getRegimenConfigData = (
  editingRowData: any,
  selectedOrgId: string,
  nccnRegimenConfigData: NccnRegimenConfig[],
): EditingConfigData | null => {
  const config = nccnRegimenConfigData.find((c) => c.id === editingRowData.id);
  if (!config) return null;

  const orgPriorityConfig =
    config.priority_config_org_specific?.[selectedOrgId];
  const allEntries = (config.icdcodes || []).filter((e) => e.code?.length > 0);
  const priorityMap = buildPriorityMap(orgPriorityConfig);
  const dxCodes = buildAndSortDxCodes(allEntries, priorityMap);

  return {
    mode: "regimen" as const,
    regimenName: config.regimen_name || "",
    regimenCode: config.regimen_code || "",
    dxCodes,
    hasPriorityConfig: hasPriorityConfig(orgPriorityConfig),
  };
};

export const useEditingConfig = (
  editingRowData: any,
  selectedOrgId: string,
  activeTab: DxCodeConfigTab,
  nccnIcdConfigData: NccnIcdConfig[],
  nccnRegimenConfigData: NccnRegimenConfig[],
): EditingConfigData | null => {
  // Memoize editingConfigData to prevent unnecessary re-renders of the side panel
  const editingConfigData = useMemo((): EditingConfigData | null => {
    if (!editingRowData) return null;

    if (activeTab === DxCodeConfigTab.DRUG) {
      return getDrugConfigData(
        editingRowData,
        selectedOrgId,
        nccnIcdConfigData,
      );
    }

    return getRegimenConfigData(
      editingRowData,
      selectedOrgId,
      nccnRegimenConfigData,
    );
  }, [
    editingRowData,
    selectedOrgId,
    activeTab,
    nccnIcdConfigData,
    nccnRegimenConfigData,
  ]);

  return editingConfigData;
};
